import type { ICondition } from './editorModel';

/**
 * Formatierter Urkundentext im Editor (#1245).
 *
 * Gespeichert wird **nie** HTML, sondern das neutrale DiNo-Modell: Absätze aus
 * Läufen, jeder Lauf entweder Text mit fett/kursiv/unterstrichen oder eine echte
 * DiNo-Variable. HTML ist ausschließlich das, was der Browser beim Tippen
 * erzeugt — hier wird es gelesen und sofort wieder verworfen.
 *
 * Das ist der Grund, warum diese Umwandlung ein eigenes Modul mit Tests ist: sie
 * ist die Grenze zwischen „was der Browser gerade macht" und „was in der Urkunde
 * steht". Alles, was sie nicht versteht, fällt weg — und zwar absichtlich, damit
 * kein fremdes Markup in den Dokumentkern gerät.
 */

export type ParagraphStyle =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'participant';

export type Alignment = 'left' | 'center' | 'right' | 'both';

export interface IRichRun {
  text?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  /**
   * Direktformatierung aus ArnoTop — Schriftart, Groesse in Punkt und Farbe als sechsstellige
   * Hexangabe.
   *
   * Der Editor bietet dafuer bewusst keine Bedienelemente: Grundtypografie kommt aus der
   * Word-Vorlage und den semantischen Absatztypen, und ein neu verfasster Text bekommt sie
   * nicht. Ein geklonter Lauf bringt sie aber mit, und dann reist sie unveraendert weiter.
   * Ohne Deutung: dass eine Stelle rot ist, heisst nur, dass sie im Altbestand rot war.
   */
  fontFamily?: string;
  fontSize?: number;
  color?: string;
  /** Die Art der Unterstreichung, wenn sie nicht die einfache ist (Word: `w:u/@w:val`). */
  underlineStyle?: string;
  /** Feldlauf: der DiNo-Selektor, nicht ein Platzhaltertext. */
  selector?: string;
  label?: string | null;
  lineBreak?: boolean;
  /**
   * Eine offene Stelle aus dem Klon: hier stand im ArnoTop-Baustein etwas, das sich nicht sicher
   * übernehmen ließ. Der Lauf hat keinen Text; er hält die Stelle im Satz und sperrt das
   * Veröffentlichen, bis sie entschieden ist (#1245, Teil 43).
   */
  migration?: IMigrationMark;
  /**
   * Bedingter Text im Satz: »Die Niederschrift wurde [den | dem] Erschienenen vorgelesen.« Jede
   * Alternative hat ihre Bedingung; ein »sonst« ist die Gegenbedingung, kein eigenes Konstrukt
   * (#1245, Teil 43). Das Backend macht daraus dieselben bedingten Textknoten wie ein Klon aus
   * einem Word-IF mitten im Satz.
   */
  choice?: IInlineChoice;
}

export interface IChoiceAlternative {
  condition: ICondition | null;
  text: string;
}

export interface IInlineChoice {
  alternatives: IChoiceAlternative[];
}

export interface IMigrationMark {
  reasonCode?: string;
  message?: string;
  detail?: Record<string, unknown>;
}

export type ListKind = 'bullet' | 'decimal' | 'lowerLetter';

export type IndentPreset = 'single' | 'double' | 'hanging';

/**
 * Was mit einem Absatz geschieht, dessen DiNo-Felder alle leer sind (#1245).
 *
 * `KEEP_TEXT` ist die Vorgabe für native Urkundentexte: ein fehlender Wert darf
 * keinen festen Satz verschlucken. `OMIT_IF_ALL_DATA_EMPTY` ist die ausdrückliche
 * Ausnahme für echte Datenzeilen („E-Mail: <Email>").
 */
export type EmptyBehavior = 'KEEP_TEXT' | 'OMIT_IF_ALL_DATA_EMPTY';

export type LineRule = 'auto' | 'exact' | 'atLeast';

export interface IRichParagraph {
  runs: IRichRun[];
  style?: ParagraphStyle | null;
  alignment?: Alignment | null;
  pageBreakBefore?: boolean;
  list?: ListKind | null;
  indent?: IndentPreset | null;
  /** Tabstopps in Zentimetern. */
  tabs?: number[];
  /** Abstand in Punkt. */
  spaceBefore?: number | null;
  spaceAfter?: number | null;
  /**
   * Zeilenabstand. Bei `lineRule` ‚auto' ein Faktor (1 = einfach), sonst ein Maß in Punkt.
   * Gemessen über alle 36 importierten Bäume bringen 1 703 von 13 108 Absätzen (13,0 %) einen
   * mit — er darf beim Bearbeiten nicht verloren gehen.
   */
  lineSpacing?: number | null;
  lineRule?: LineRule | null;
  /** Absatzrahmen aus einem Klon (‚top', ‚bottom', …). Wird erhalten, nicht angeboten. */
  borders?: string[];
  keepNext?: boolean;
  emptyBehavior?: EmptyBehavior | null;
  /**
   * Der Absatz geht im Quelltext weiter: kein Absatzende nach diesem Stück, der nächste Knoten
   * setzt den Satz fort (so zerlegt der Klon einen Satz mit Bedingung darin). Kommt aus dem
   * Backend und bleibt beim Bearbeiten erhalten.
   */
  continues?: boolean;
}

/** Was der Absatztyp im Editor heißt. Die Bedeutung, nicht der Word-Stilname. */
export const PARAGRAPH_STYLE_LABEL: Record<ParagraphStyle, string> = {
  heading1: 'Kapitelüberschrift',
  heading2: 'Abschnittsüberschrift',
  heading3: 'Unterüberschrift',
  participant: 'Beteiligte',
};

/** Listenarten, die der gemeinsame Renderer zuverlässig als echte Word-Liste setzt. */
export const LIST_LABEL: Record<ListKind, string> = {
  bullet: '• Aufzählung',
  decimal: '1. Nummeriert',
  lowerLetter: 'a) Buchstaben',
};

/**
 * Zeilenabstände als Vorgaben. Ein Notariat wählt »einfach« oder »1,5-zeilig«, es rechnet
 * nicht in Twips. Ein exakt gesetzter Abstand aus einem Klon bekommt stattdessen eine eigene
 * Zeile (siehe `lineSpacingChoice`): erhalten, aber nicht als Gestaltungsoption angeboten.
 */
export const LINE_SPACING_PRESETS: {
  id: string;
  label: string;
  lineSpacing: number | null;
  lineRule: LineRule | null;
}[] = [
  {
    id: 'template',
    label: 'Wie die Vorlage',
    lineSpacing: null,
    lineRule: null,
  },
  { id: 'single', label: 'Einfach', lineSpacing: 1, lineRule: 'auto' },
  { id: 'onehalf', label: '1,5-zeilig', lineSpacing: 1.5, lineRule: 'auto' },
  { id: 'double', label: 'Doppelt', lineSpacing: 2, lineRule: 'auto' },
];

/** Welche Zeile gerade gilt — und wie sie heißt, wenn es keine der Vorgaben ist. */
export const lineSpacingChoice = (
  paragraph: IRichParagraph
): { id: string; label: string } => {
  const wert = paragraph.lineSpacing;
  if (wert === null || wert === undefined) {
    return { id: 'template', label: 'Wie die Vorlage' };
  }
  const regel = paragraph.lineRule ?? 'auto';
  const vorgabe = LINE_SPACING_PRESETS.find(
    eintrag => eintrag.lineSpacing === wert && eintrag.lineRule === regel
  );
  if (vorgabe) {
    return { id: vorgabe.id, label: vorgabe.label };
  }
  const zahl = String(wert).replace('.', ',');
  return {
    id: 'carried',
    label:
      regel === 'exact'
        ? `Genau ${zahl} Punkt (übernommen)`
        : regel === 'atLeast'
          ? `Mindestens ${zahl} Punkt (übernommen)`
          : `${zahl}-fach (übernommen)`,
  };
};

/** Einzüge als Vorgaben — eine Twips-Eingabe ist keine Oberfläche für ein Notariat. */
export const INDENT_LABEL: Record<IndentPreset, string> = {
  single: 'Einfach',
  double: 'Doppelt',
  hanging: 'Hängend',
};

export const EMPTY_BEHAVIOR_LABEL: Record<EmptyBehavior, string> = {
  KEEP_TEXT: 'Absatz trotzdem anzeigen',
  OMIT_IF_ALL_DATA_EMPTY: 'Absatz auslassen',
};

export const ALIGNMENT_LABEL: Record<Alignment, string> = {
  left: 'linksbündig',
  center: 'zentriert',
  right: 'rechtsbündig',
  both: 'Blocksatz',
};

/** Attribute, an denen eine eingefügte Variable im Editor erkennbar ist. */
export const FIELD_TAG = 'span';
export const FIELD_ATTRIBUTE = 'data-selector';
export const FIELD_LABEL_ATTRIBUTE = 'data-label';

/**
 * Attribute, an denen mitgereiste Direktformatierung erkennbar ist.
 *
 * Gelesen wird aus diesen Attributen, nicht aus `style`: was ein Browser daraus macht —
 * `rgb(255, 0, 0)` statt `FF0000`, Anfuehrungszeichen um Schriftnamen — waere beim naechsten
 * Speichern ein anderer Wert. Das Inline-CSS steht nur daneben, damit man die Formatierung sieht.
 */
export const FONT_FAMILY_ATTRIBUTE = 'data-font-family';
export const FONT_SIZE_ATTRIBUTE = 'data-font-size';
export const COLOR_ATTRIBUTE = 'data-color';
export const UNDERLINE_STYLE_ATTRIBUTE = 'data-underline-style';
/** Bedingter Text im Satz im Bearbeitungsfeld: die Alternativen reisen als JSON im Attribut mit. */
export const CHOICE_ATTRIBUTE = 'data-choice';
export const CHOICE_CLASS = 'dino-choice';

/** »[den | dem]« — was an der Stelle stehen kann. Eine leere Alternative ist »—«. */
export const choiceLabel = (choice: IInlineChoice | undefined): string =>
  `[${(choice?.alternatives ?? [])
    .map(alternative => alternative.text.trim() || '—')
    .join(' | ')}]`;

/** Offene Stelle im Bearbeitungsfeld: die Angaben reisen als JSON im Attribut mit. */
export const MIGRATION_ATTRIBUTE = 'data-migration';
export const MIGRATION_CLASS = 'dino-migration';

/** Wie eine offene Stelle im Text heißt — ohne Codes. */
export const migrationLabel = (mark: IMigrationMark | undefined): string => {
  const variable = mark?.detail?.variable;
  return typeof variable === 'string' && variable
    ? `offene Stelle (${variable})`
    : 'offene Stelle';
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Geschützte Leerzeichen (#1245, Teil 46).
 *
 * Tippt man neben einer Marke (Datenfeld, bedingter Text, offene Stelle), setzt der Browser statt
 * des Leerzeichens ein geschütztes — es landete bis in die Word-Urkunde (»wurde\u00a0dem\u00a0
 * Erschienenen«, kein Zeilenumbruch mehr an dieser Stelle; Host-Regression S10). Gewollte
 * geschützte Leerzeichen gibt es aber auch (»Nr. 3«, »§ 5« in Klonen). Deshalb: was aus dem
 * gespeicherten Modell kommt, wird im Editor gekennzeichnet und bleibt; ein ungekennzeichnetes
 * direkt an einer Marke stammt vom Browser und wird wieder ein normales Leerzeichen.
 */
export const NBSP_ATTRIBUTE = 'data-dino-nbsp';
const NBSP = '\u00a0';

const markNbsp = (html: string): string =>
  html.replace(/\u00a0/g, `<span ${NBSP_ATTRIBUTE}="1">${NBSP}</span>`);

/** Ein Lauf ohne Inhalt trägt nichts zur Urkunde bei. */
const isEmptyRun = (run: IRichRun): boolean =>
  !run.selector &&
  !run.lineBreak &&
  !run.migration &&
  !run.choice &&
  !(run.text ?? '');

/**
 * Gleich formatierte Nachbarn zusammenfassen.
 *
 * Der Browser zerlegt beim Tippen gern in viele Textknoten; ohne dieses
 * Zusammenfassen entstünden bei jedem Tastendruck andere Läufe und damit eine
 * neue Bausteinversion, obwohl sich nichts geändert hat.
 */
export const mergeRuns = (runs: IRichRun[]): IRichRun[] => {
  const result: IRichRun[] = [];
  runs.forEach(run => {
    if (isEmptyRun(run)) {
      return;
    }
    const previous = result[result.length - 1];
    const mergeable =
      previous &&
      !previous.selector &&
      !previous.lineBreak &&
      !previous.migration &&
      !previous.choice &&
      !run.selector &&
      !run.lineBreak &&
      !run.migration &&
      !run.choice &&
      Boolean(previous.bold) === Boolean(run.bold) &&
      Boolean(previous.italic) === Boolean(run.italic) &&
      Boolean(previous.underline) === Boolean(run.underline) &&
      // Auch die Direktformatierung muss gleich sein, sonst faerbte das Zusammenfassen
      // Nachbartext mit ein.
      (previous.fontFamily ?? null) === (run.fontFamily ?? null) &&
      (previous.fontSize ?? null) === (run.fontSize ?? null) &&
      (previous.color ?? null) === (run.color ?? null) &&
      (previous.underlineStyle ?? null) === (run.underlineStyle ?? null);
    if (mergeable) {
      previous.text = (previous.text ?? '') + (run.text ?? '');
      return;
    }
    result.push({ ...run });
  });
  return result;
};

/** Nur die gesetzten Formatmerkmale — leere Felder blähen den gespeicherten Baum auf. */
export const cleanRun = (run: IRichRun): IRichRun => {
  const result: IRichRun = {};
  if (run.selector) {
    result.selector = run.selector;
    if (run.label) {
      result.label = run.label;
    }
  } else if (run.lineBreak) {
    result.lineBreak = true;
  } else if (run.migration) {
    result.migration = run.migration;
  } else if (run.choice) {
    result.choice = run.choice;
  } else {
    result.text = run.text ?? '';
  }
  if (run.bold) {
    result.bold = true;
  }
  if (run.italic) {
    result.italic = true;
  }
  if (run.underline) {
    result.underline = true;
  }
  if (run.fontFamily) {
    result.fontFamily = run.fontFamily;
  }
  if (run.fontSize !== undefined && run.fontSize !== null) {
    result.fontSize = run.fontSize;
  }
  if (run.color) {
    result.color = run.color;
  }
  if (run.underlineStyle) {
    result.underlineStyle = run.underlineStyle;
  }
  return result;
};

interface IFormatState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  fontFamily?: string;
  fontSize?: number;
  color?: string;
  underlineStyle?: string;
}

/** Direktformatierung eines Elements — aus den Datenattributen, nicht aus `style`. */
const withDirectFormat = (
  state: IFormatState,
  element: Element
): IFormatState => {
  let result = state;
  const family = element.getAttribute(FONT_FAMILY_ATTRIBUTE);
  if (family) {
    result = { ...result, fontFamily: family };
  }
  const size = element.getAttribute(FONT_SIZE_ATTRIBUTE);
  if (size && Number.isFinite(Number(size))) {
    result = { ...result, fontSize: Number(size) };
  }
  const colour = element.getAttribute(COLOR_ATTRIBUTE);
  if (colour) {
    result = { ...result, color: colour };
  }
  const underlineStyle = element.getAttribute(UNDERLINE_STYLE_ATTRIBUTE);
  if (underlineStyle) {
    result = { ...result, underlineStyle };
  }
  return result;
};

const withTag = (state: IFormatState, tag: string): IFormatState => {
  switch (tag) {
    case 'b':
    case 'strong':
      return { ...state, bold: true };
    case 'i':
    case 'em':
      return { ...state, italic: true };
    case 'u':
    case 'ins':
      return { ...state, underline: true };
    default:
      return state;
  }
};

const withStyle = (state: IFormatState, element: Element): IFormatState => {
  // execCommand schreibt je nach Browser Tags oder Stilattribute — beides lesen.
  const style = (element as HTMLElement).style;
  if (!style) {
    return state;
  }
  let result = state;
  if (style.fontWeight === 'bold' || Number(style.fontWeight) >= 600) {
    result = { ...result, bold: true };
  }
  if (style.fontStyle === 'italic') {
    result = { ...result, italic: true };
  }
  if ((style.textDecoration || '').includes('underline')) {
    result = { ...result, underline: true };
  }
  return result;
};

/**
 * Was im Editor steht, als Läufe.
 *
 * Alles, was hier nicht verstanden wird — Schriftarten, Farben, Bilder, fremdes
 * Markup aus der Zwischenablage — fällt weg. Es soll wegfallen: der Dokumentkern
 * ist kein HTML-Speicher.
 */
export const runsFromElement = (element: Element | null): IRichRun[] => {
  if (!element) {
    return [];
  }
  const runs: IRichRun[] = [];
  // Läufe mit einem gekennzeichneten (gewollten) geschützten Leerzeichen.
  const keep = new Set<IRichRun>();

  const walk = (node: Node, state: IFormatState): void => {
    node.childNodes.forEach(child => {
      if (child.nodeType === 3) {
        const text = child.textContent ?? '';
        if (text) {
          runs.push({ text, ...state });
        }
        return;
      }
      if (child.nodeType !== 1) {
        return;
      }
      const child_ = child as Element;
      const tag = child_.tagName.toLowerCase();
      if (tag === 'br') {
        runs.push({ lineBreak: true });
        return;
      }
      if (child_.getAttribute(NBSP_ATTRIBUTE)) {
        const kept: IRichRun = { text: NBSP, ...state };
        keep.add(kept);
        runs.push(kept);
        return;
      }
      const choice = child_.getAttribute(CHOICE_ATTRIBUTE);
      if (choice) {
        try {
          runs.push({ choice: JSON.parse(choice) as IInlineChoice, ...state });
        } catch {
          /* unlesbar — lieber weglassen als eine falsche Bedingung speichern */
        }
        return;
      }
      const migration = child_.getAttribute(MIGRATION_ATTRIBUTE);
      if (migration) {
        try {
          runs.push({ migration: JSON.parse(migration) as IMigrationMark });
        } catch {
          runs.push({ migration: {} });
        }
        return;
      }
      const selector = child_.getAttribute(FIELD_ATTRIBUTE);
      if (selector) {
        runs.push({
          selector,
          label: child_.getAttribute(FIELD_LABEL_ATTRIBUTE) || null,
          ...state,
        });
        return;
      }
      walk(
        child_,
        withDirectFormat(withStyle(withTag(state, tag), child_), child_)
      );
    });
  };

  walk(element, { bold: false, italic: false, underline: false });

  // Ein ungekennzeichnetes geschütztes Leerzeichen direkt an einer Marke hat der Browser gesetzt.
  const isMark = (run: IRichRun | undefined) =>
    Boolean(run && (run.selector || run.choice || run.migration));
  const cleaned = runs.map((run, index) => {
    if (!run.text || keep.has(run)) {
      return run;
    }
    let text = run.text;
    if (isMark(runs[index - 1]) && text.startsWith(NBSP)) {
      text = ' ' + text.slice(1);
    }
    if (isMark(runs[index + 1]) && text.endsWith(NBSP)) {
      text = text.slice(0, -1) + ' ';
    }
    return text === run.text ? run : { ...run, text };
  });
  return mergeRuns(cleaned).map(cleanRun);
};

/** Dieselbe Umwandlung für einen HTML-Schnipsel — für Tests und eingefügten Text. */
export const runsFromHtml = (html: string): IRichRun[] => {
  if (typeof document === 'undefined') {
    return [];
  }
  const holder = document.createElement('div');
  holder.innerHTML = html;
  return runsFromElement(holder);
};

/** Läufe als HTML für das Bearbeitungsfeld — nie gespeichert, nur angezeigt. */
export const htmlFromRuns = (runs: IRichRun[]): string => {
  if (runs.length === 0) {
    return '';
  }
  return runs
    .map(run => {
      if (run.lineBreak) {
        return '<br>';
      }
      if (run.choice) {
        return `<span ${CHOICE_ATTRIBUTE}="${escapeHtml(JSON.stringify(run.choice))}" contenteditable="false" class="${CHOICE_CLASS}">${escapeHtml(
          choiceLabel(run.choice)
        )}</span>`;
      }
      if (run.migration) {
        // Nicht editierbar und sichtbar: wer tippt, schreibt daneben, nicht darüber.
        return `<span ${MIGRATION_ATTRIBUTE}="${escapeHtml(JSON.stringify(run.migration))}" contenteditable="false" class="${MIGRATION_CLASS}">${escapeHtml(
          migrationLabel(run.migration)
        )}</span>`;
      }
      let inner: string;
      if (run.selector) {
        inner = `<${FIELD_TAG} ${FIELD_ATTRIBUTE}="${escapeHtml(run.selector)}" ${FIELD_LABEL_ATTRIBUTE}="${escapeHtml(
          run.label ?? ''
        )}" contenteditable="false" class="dino-field">${escapeHtml(
          run.label || run.selector
        )}</${FIELD_TAG}>`;
      } else {
        inner = markNbsp(escapeHtml(run.text ?? ''));
      }
      if (run.underlineStyle) {
        // `<u>` zeigt nur die einfache Linie; die Art steht im Attribut und wird von dort
        // gelesen. Das CSS macht eine doppelte Linie auch im Editor sichtbar.
        inner = `<span ${UNDERLINE_STYLE_ATTRIBUTE}="${escapeHtml(run.underlineStyle)}" style="text-decoration:underline ${run.underlineStyle === 'double' ? 'double' : 'solid'}">${inner}</span>`;
      }
      if (run.fontFamily || run.fontSize !== undefined || run.color) {
        // Die Attribute tragen die Wahrheit, das Inline-CSS macht sie sichtbar.
        const attributes: string[] = [];
        const styles: string[] = [];
        if (run.fontFamily) {
          attributes.push(
            `${FONT_FAMILY_ATTRIBUTE}="${escapeHtml(run.fontFamily)}"`
          );
          styles.push(`font-family:${run.fontFamily}`);
        }
        if (run.fontSize !== undefined && run.fontSize !== null) {
          attributes.push(
            `${FONT_SIZE_ATTRIBUTE}="${escapeHtml(String(run.fontSize))}"`
          );
          styles.push(`font-size:${run.fontSize}pt`);
        }
        if (run.color) {
          attributes.push(`${COLOR_ATTRIBUTE}="${escapeHtml(run.color)}"`);
          styles.push(`color:#${run.color}`);
        }
        inner = `<span ${attributes.join(' ')} style="${styles.join(';')}">${inner}</span>`;
      }
      if (run.underline) {
        inner = `<u>${inner}</u>`;
      }
      if (run.italic) {
        inner = `<i>${inner}</i>`;
      }
      if (run.bold) {
        inner = `<b>${inner}</b>`;
      }
      return inner;
    })
    .join('');
};

/** Der Text eines Absatzes ohne Formatierung — für Zusammenfassungen. */
export const plainTextOfParagraph = (paragraph: IRichParagraph): string =>
  (paragraph.runs ?? [])
    .map(run => {
      if (run.selector) {
        return run.label || run.selector;
      }
      if (run.lineBreak) {
        return ' ';
      }
      if (run.migration) {
        return `[${migrationLabel(run.migration)}]`;
      }
      if (run.choice) {
        return choiceLabel(run.choice);
      }
      return run.text ?? '';
    })
    .join('');

export const plainTextOfParagraphs = (paragraphs: IRichParagraph[]): string =>
  paragraphs.map(plainTextOfParagraph).join('\n');

/**
 * Die Absätze eines Textknotens — auch für den älteren Bestand mit reinem Text.
 *
 * Dieselbe Regel wie im Backend: ein Knoten ohne Absätze wird als schlichter Text
 * gelesen. Dadurch gibt es im Editor nur einen Weg.
 */
export const paragraphsOfNode = (node: {
  paragraphs?: IRichParagraph[];
  text?: string;
}): IRichParagraph[] => {
  if (node.paragraphs && node.paragraphs.length > 0) {
    return node.paragraphs;
  }
  const text = node.text ?? '';
  if (!text) {
    return [{ runs: [] }];
  }
  return text.split('\n').map(line => ({ runs: line ? [{ text: line }] : [] }));
};

export const newParagraph = (
  style?: ParagraphStyle | null
): IRichParagraph => ({
  runs: [],
  style: style ?? null,
  alignment: null,
  pageBreakBefore: false,
  list: null,
  indent: null,
  tabs: [],
  spaceBefore: null,
  spaceAfter: null,
  // Kein erfundener Standardwert: ohne Angabe bestimmt die Word-Vorlage den Zeilenabstand.
  lineSpacing: null,
  lineRule: null,
  keepNext: false,
  // Vorgabe ist „anzeigen": ein fehlendes Feld darf keinen Urkundentext verschlucken.
  emptyBehavior: null,
});

/** Eine Variable als Lauf, wie der Selector-Picker sie liefert. */
export const fieldRun = (
  selector: string,
  label?: string | null
): IRichRun => ({
  selector,
  label: label || null,
});

/** Beschriftung eines Absatzes in der Übersichtszeile. */
export const paragraphSummary = (paragraph: IRichParagraph): string => {
  const text = plainTextOfParagraph(paragraph).trim();
  if (text) {
    return text.length > 80 ? `${text.slice(0, 79)}…` : text;
  }
  return '(leerer Absatz)';
};

/** Ob die Ausrichtung bei diesem Absatz überhaupt wirkt. */
export const alignmentApplies = (paragraph: IRichParagraph): boolean =>
  !paragraph.style;

/**
 * Ob direktes Absatzlayout bei diesem Absatz überhaupt wirkt.
 *
 * Trägt der Absatz einen Stil, bestimmt dessen Definition im Zieldokument Einzug,
 * Tabstopps und Abstände — dieselbe Regel wie bei der Ausrichtung. Der Editor
 * zeigt die Felder dann gesperrt statt sie wirkungslos anzubieten.
 */
export const layoutApplies = (paragraph: IRichParagraph): boolean =>
  !paragraph.style;

/** Hat der Absatz überhaupt ein DiNo-Feld? Ohne eines kann er nie „leer" sein. */
export const hasDataField = (paragraph: IRichParagraph): boolean =>
  paragraph.runs.some(run => Boolean(run.selector));

/** Wirksames Verhalten bei leeren Daten — ohne Angabe bleibt der Absatz stehen. */
export const emptyBehaviorOf = (paragraph: IRichParagraph): EmptyBehavior =>
  paragraph.emptyBehavior === 'OMIT_IF_ALL_DATA_EMPTY'
    ? 'OMIT_IF_ALL_DATA_EMPTY'
    : 'KEEP_TEXT';

/** Tabstopp-Eingabe („2; 5,5") in Zentimeter — ungültige Angaben fallen weg. */
export const parseTabs = (input: string): number[] => {
  const values = (input || '')
    // Nur Semikolon und Leerraum trennen — das Komma ist hier das Dezimalzeichen („5,5").
    .split(/[;\s]+/)
    .map(part => part.trim().replace(',', '.'))
    .filter(Boolean)
    .map(Number)
    .filter(value => Number.isFinite(value) && value > 0 && value <= 20);
  return Array.from(new Set(values)).sort((a, b) => a - b);
};

/** Umkehrung für das Eingabefeld. */
export const formatTabs = (tabs?: number[]): string =>
  (tabs ?? []).map(value => String(value).replace('.', ',')).join('; ');
